/** User notes on own-ledger transactions and cash flows — History + Analytics type badges. */

import { cashFlowIdFromHistoryRow, isCashFlowHistoryRow } from "./historyCashFlows";
import type { HistoryRelatedTxn } from "./historyRelated";
import { saveOwnLedgerTransactionNote, updateOwnLedgerCashFlow } from "./ownLedgerClient";
import { afterWheelLedgerChange } from "./wheelActions";
import { showToast } from "./toast";
import { esc } from "./watchlistData";

const MODAL_ID = "sfe-txn-note-modal";
const USER_NOTE_KIND = "user";
const NOTE_MAX = 2000;

export interface LedgerNoteSource {
  id?: number;
  instrument_type?: string;
  note?: string | null;
  notes_preview?: string | null;
  notes?: Array<{ body?: string | null; kind?: string | null }>;
  cash_flow?: boolean;
}

/** Importer/system cash memos — not a user-attached note. */
function isImporterCashNote(note: string): boolean {
  if (!note) return false;
  return (
    /^Robinhood\b/i.test(note) ||
    /^Forecast tax\b/i.test(note) ||
    /\[batch:/i.test(note) ||
    /migrated from own-capital/i.test(note) ||
    / conversion$/i.test(note)
  );
}

/** Attached user memo only — not inferred transfer labels or cash category names. */
export function ledgerAttachedNote(txn: LedgerNoteSource): string {
  if (isCashFlowHistoryRow(txn as HistoryRelatedTxn) || txn.instrument_type === "cash") {
    const note = String(txn.note ?? "").trim();
    return note && !isImporterCashNote(note) ? note : "";
  }
  const rows = Array.isArray(txn.notes) ? txn.notes : [];
  const user = rows.find(
    (row) => row.kind === USER_NOTE_KIND && String(row.body ?? "").trim(),
  );
  return user?.body ? String(user.body).trim() : "";
}

function noteHoverTitle(note: string, canEdit: boolean): string {
  if (note) return note.replace(/\s+/g, " ").slice(0, 400);
  return canEdit ? "Add a note" : "";
}

export function renderTxnNoteBadge(opts: {
  label: string;
  className: string;
  note?: string | null;
  txnId?: number | null;
  cashFlowId?: number | null;
}): string {
  const label = opts.label || "—";
  const note = String(opts.note ?? "").trim();
  const cashFlowId = Number(opts.cashFlowId) || 0;
  const txnId = Number(opts.txnId) || 0;
  const canEdit = cashFlowId > 0 || txnId > 0;
  const mark = note
    ? `<span class="sfe-txn-note-mark" aria-hidden="true"><svg viewBox="0 0 16 16" width="10" height="10"><path fill="currentColor" d="M3.25 1.25h7.1L13.75 4.7v9.05c0 .55-.45 1-1 1h-9.5c-.55 0-1-.45-1-1V2.25c0-.55.45-1 1-1Zm6.85.85v2.35c0 .3.25.55.55.55h2.4L10.1 2.1ZM5 7.15h6v1.15H5V7.15Zm0 2.4h6v1.15H5V9.55Zm0 2.4h4.2v1.15H5V11.95Z"/></svg></span>`
    : "";
  const title = noteHoverTitle(note, canEdit);
  const titleAttr = title ? ` title="${esc(title)}"` : "";
  const noteAttr = note ? ` data-sfe-note="${esc(note)}"` : "";
  const hostClass = `${opts.className} sfe-txn-note-host${note ? " sfe-txn-note-has" : ""}`;
  const targetAttr = !canEdit
    ? ""
    : cashFlowId > 0
      ? ` data-sfe-cash-note="${cashFlowId}"`
      : ` data-sfe-txn-note="${txnId}"`;
  const labelAttr = canEdit ? ` data-sfe-note-label="${esc(label)}"` : "";
  return `<span class="${hostClass}"${targetAttr}${noteAttr}${labelAttr}${titleAttr}>${esc(label)}${mark}</span>`;
}

function closeNoteModal(): void {
  document.getElementById(MODAL_ID)?.classList.remove("sfe-modal-open");
}

export function openTxnNoteModal(opts: {
  label: string;
  note?: string | null;
  txnId?: number | null;
  cashFlowId?: number | null;
  onSaved?: () => void | Promise<void>;
}): void {
  const cashFlowId = Number(opts.cashFlowId) || 0;
  const txnId = Number(opts.txnId) || 0;
  if (!(cashFlowId > 0 || txnId > 0)) {
    showToast("This row cannot take a note.", "error");
    return;
  }

  let overlay = document.getElementById(MODAL_ID);
  if (!overlay) {
    overlay = document.createElement("div");
    overlay.id = MODAL_ID;
    overlay.className = "sfe-modal-overlay";
    overlay.innerHTML = `
      <div class="sfe-modal sfe-txn-note-modal" role="dialog" aria-labelledby="sfe-txn-note-title">
        <div class="sfe-modal-header">
          <h3 id="sfe-txn-note-title">Note</h3>
          <button type="button" class="sfe-modal-close" data-action="txn-note-close" aria-label="Close">✕</button>
        </div>
        <p class="muted small" data-txn-note-lead></p>
        <form class="sfe-txn-note-form" data-txn-note-form>
          <label class="sfe-modal-field">Note
            <textarea name="note" rows="4" maxlength="${NOTE_MAX}" placeholder="Optional memo for this transaction" data-txn-note-body></textarea>
          </label>
          <div class="sfe-modal-actions">
            <button type="button" class="ghost small" data-action="txn-note-clear">Clear</button>
            <span class="sfe-history-cash-edit-gap"></span>
            <button type="button" class="ghost small" data-action="txn-note-cancel">Cancel</button>
            <button type="submit" class="small">Save</button>
          </div>
        </form>
      </div>`;
    document.body.appendChild(overlay);
    overlay.addEventListener("click", (event) => {
      const target = event.target;
      if (!(target instanceof HTMLElement)) return;
      if (target === overlay) {
        closeNoteModal();
        return;
      }
      const action = target.closest<HTMLElement>("[data-action]")?.dataset.action;
      if (action === "txn-note-close" || action === "txn-note-cancel") closeNoteModal();
    });
  }

  const title = overlay.querySelector("#sfe-txn-note-title");
  const lead = overlay.querySelector("[data-txn-note-lead]");
  const form = overlay.querySelector<HTMLFormElement>("[data-txn-note-form]");
  const textarea = overlay.querySelector<HTMLTextAreaElement>("[data-txn-note-body]");
  const clearBtn = overlay.querySelector<HTMLButtonElement>("[data-action='txn-note-clear']");
  if (!title || !lead || !form || !textarea || !clearBtn) return;

  title.textContent = `Note · ${opts.label}`;
  lead.textContent = cashFlowId > 0 ? "Saved on this cash ledger row." : `Transaction #${txnId}.`;
  textarea.value = String(opts.note ?? "").trim();

  const save = async (body: string): Promise<void> => {
    try {
      if (cashFlowId > 0) {
        await updateOwnLedgerCashFlow(cashFlowId, { note: body || null });
      } else {
        await saveOwnLedgerTransactionNote(txnId, body);
      }
      showToast(body ? "Note saved." : "Note cleared.", "success");
      closeNoteModal();
      await afterWheelLedgerChange(opts.onSaved ?? undefined);
    } catch {
      showToast("Could not save note.", "error");
    }
  };

  form.onsubmit = (event) => {
    event.preventDefault();
    void save(textarea.value.trim());
  };
  clearBtn.onclick = () => {
    textarea.value = "";
    void save("");
  };

  overlay.classList.add("sfe-modal-open");
  textarea.focus();
  textarea.setSelectionRange(textarea.value.length, textarea.value.length);
}

let clickBound = false;

export function ensureTxnNoteClickBinding(): void {
  if (clickBound) return;
  clickBound = true;
  document.addEventListener("click", (event) => {
    const trigger = (event.target as HTMLElement | null)?.closest<HTMLElement>(
      "[data-sfe-txn-note], [data-sfe-cash-note]",
    );
    if (!trigger) return;
    event.preventDefault();
    event.stopPropagation();
    const cashFlowId = Number(trigger.dataset.sfeCashNote) || 0;
    const txnId = Number(trigger.dataset.sfeTxnNote) || 0;
    openTxnNoteModal({
      label: trigger.dataset.sfeNoteLabel || trigger.textContent?.trim() || "Note",
      note: trigger.dataset.sfeNote ?? "",
      cashFlowId: cashFlowId > 0 ? cashFlowId : null,
      txnId: txnId > 0 ? txnId : null,
    });
  });
}

export function noteTargetFromHistoryTxn(txn: HistoryRelatedTxn): {
  txnId: number | null;
  cashFlowId: number | null;
} {
  const cashFlowId = cashFlowIdFromHistoryRow(txn);
  const id = Number(txn.id) || 0;
  return {
    cashFlowId,
    txnId: cashFlowId ? null : id > 0 ? id : null,
  };
}
